import { useEffect, useRef, useState } from "react";
import { KeyRound, LogOut, UserRound } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

export default function AccountMenu({ user, onChangePassword, onLogout }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const profileImage = user?.avatar_url || user?.profile_image_url || null;
  const initial = user?.name?.trim()?.slice(0, 1) || null;
  const role = user?.role === "OWNER" ? "المالك" : "موظف";

  useEffect(() => {
    if (!open) return undefined;
    const restoreAvatarFocus = () => {
      window.requestAnimationFrame(() => avatarButtonRef.current?.focus());
    };
    const closeOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
        restoreAvatarFocus();
      }
    };
    const closeEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        restoreAvatarFocus();
      }
    };
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [open]);

  const openPasswordDialog = () => {
    setOpen(false);
    onChangePassword();
  };

  const logout = () => {
    setOpen(false);
    onLogout();
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        ref={avatarButtonRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="rounded-full outline-none ring-offset-2 transition-shadow hover:ring-2 hover:ring-orange-200 focus:ring-2 focus:ring-orange-400"
        aria-label="فتح قائمة الحساب"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-avatar-button"
      >
        <Avatar className="h-9 w-9 border border-orange-100 bg-orange-50">
          {profileImage && <AvatarImage src={profileImage} alt=""/>}
          <AvatarFallback className="bg-orange-50 text-sm font-extrabold text-orange-700">
            {initial || <UserRound size={17} aria-hidden="true"/>}
          </AvatarFallback>
        </Avatar>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 z-50 w-56 rounded-2xl border border-orange-100 bg-white p-2 shadow-lg"
          data-testid="account-menu"
        >
          <div className="border-b border-orange-100 px-3 py-2" data-testid="account-menu-user-info">
            <div className="truncate text-sm font-extrabold text-gray-800" data-testid="account-menu-name">
              {user?.name || "الحساب"}
            </div>
            <div className="mt-0.5 text-xs text-gray-500" data-testid="account-menu-role">
              {role}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={openPasswordDialog}
            className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-right text-sm font-bold text-gray-700 hover:bg-orange-50 focus:bg-orange-50"
            data-testid="account-change-password"
          >
            <KeyRound size={16} className="text-orange-600"/>
            تغيير كلمة المرور
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-right text-sm font-bold text-red-600 hover:bg-red-50 focus:bg-red-50"
            data-testid="account-menu-logout"
          >
            <LogOut size={16}/>
            تسجيل الخروج
          </button>
        </div>
      )}
    </div>
  );
}